import { useEffect, useRef } from 'react';
import { toResult } from '@/infrastructure/api/v1/client';
import { BackendAuth, v1App } from '@/infrastructure/clients/BackendClient';
import { toast } from '@/components/cms/ui/sonner';

// The startup reporter owns durable sightings independently of this update gate.

const UPDATE_TOAST_ID = 'lureo-new-version';

async function fetchMinimumVersion(): Promise<string | null> {
  const { data } = await toResult(v1App.GET('/api/v1/version', { cache: 'no-store' }));
  return data?.minimumVersion ?? null;
}

function deleteUnityIDB(): Promise<void> {
  // Unity WebGL keeps decompressed bundles in an IndexedDB database called
  // 'UnityCache' keyed by URL — Service Worker cache APIs don't touch it.
  return new Promise((resolve) => {
    try {
      const req = indexedDB.deleteDatabase('UnityCache');
      req.onsuccess = req.onerror = req.onblocked = () => resolve();
    } catch {
      resolve();
    }
  });
}

async function applyVersion(minimumVersion: string, storedVersion: string | null) {
  console.log(`--↻ Version changed: ${storedVersion || 'none'} → ${minimumVersion}. Clearing cache...`);

  const names = await caches.keys();
  await Promise.all(names.map(name => caches.delete(name)));
  console.log(`--→ Cleared ${names.length} cache(s)`);

  await deleteUnityIDB();
  console.log('--→ Cleared Unity IndexedDB cache');

  localStorage.setItem('appVersion', minimumVersion);

  if (storedVersion !== null) {
    // Signs out every tab, and revokes the session on the server
    await BackendAuth.signOut();
    console.log('--↻ Reloading to apply new version...');
    window.location.reload();
  }
}

// Coming back to a tab, the page may hold unsaved work, so the person is asked before a new version applies
async function handleVersionChange(onLoad: boolean) {
  const minimumVersion = await fetchMinimumVersion();
  if (!minimumVersion) return;

  const storedVersion = localStorage.getItem('appVersion');

  if (storedVersion === minimumVersion) {
    console.log(`--→ Using cached version ${minimumVersion}`);
    return;
  }
  if (onLoad || storedVersion === null) {
    await applyVersion(minimumVersion, storedVersion);
    return;
  }
  toast('A new version of Lureo is available', {
    id: UPDATE_TOAST_ID,
    description: 'Save your work, then reload. You will need to sign in again.',
    duration: Infinity,
    action: { label: 'Reload', onClick: () => void applyVersion(minimumVersion, storedVersion) },
  });
}

const CacheBuster = () => {
  const hasCheckedVersion = useRef(false);

  // One-time version check on mount
  useEffect(() => {
    if (hasCheckedVersion.current) return;
    hasCheckedVersion.current = true;
    handleVersionChange(true);
  }, []);

  // Persistent listener — separate from the one-time check so strict mode can't kill it
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') handleVersionChange(false);
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  return null;
};

export default CacheBuster;
